import { useEffect, useState } from "react";
import { Download, Loader2, Check, X } from "lucide-react";
import { api, formatApiError, API } from "../lib/api";

function trackEvent(name) {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.gtag === "function") window.gtag("event", name);
    (window.dataLayer = window.dataLayer || []).push({ event: name });
  } catch (e) { /* no-op */ }
}

export default function CarpetCardDownload({
  label = "Download the Carpet Card",
  className = "",
  testid = "carpet-card-trigger",
  showIcon = true,
}) {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus({ state: "loading", message: "" });
    try {
      const { data } = await api.post("/lead-magnet/carpet-card", { first_name: firstName, email });
      setDownloadUrl(`${API}/download/carpet-card`);
      setStatus({ state: "success", message: data.message });
      trackEvent("carpet_keep_vs_replace_card_lead");
    } catch (err) {
      setStatus({ state: "error", message: formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again." });
    }
  };

  return (
    <>
      <button type="button" data-testid={testid} onClick={() => setOpen(true)} className={className}>
        {showIcon && <Download className="h-4 w-4" />} {label}
      </button>

      {open && (
        <div
          data-testid="carpet-card-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md bg-base border border-border shadow-2xl">
            <span aria-hidden className="absolute -top-px -left-px h-3.5 w-3.5 border-t-2 border-l-2 border-primary/60" />
            <span aria-hidden className="absolute -top-px -right-px h-3.5 w-3.5 border-t-2 border-r-2 border-primary/60" />
            <span aria-hidden className="absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2 border-primary/60" />
            <span aria-hidden className="absolute -bottom-px -right-px h-3.5 w-3.5 border-b-2 border-r-2 border-primary/60" />
            <button
              type="button"
              data-testid="carpet-card-close"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {status.state === "success" ? (
              <div data-testid="carpet-card-success" className="p-7">
                <div className="flex items-center gap-2 text-primary">
                  <Check className="h-5 w-5" /><span className="font-display text-lg">Check your inbox</span>
                </div>
                <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
                  We just emailed your Carpet: Keep vs. Replace card and subscribed you to Greenpapers. You can also download it right now:
                </p>
                <a
                  data-testid="carpet-card-download"
                  href={downloadUrl}
                  rel="noopener"
                  onClick={() => trackEvent("carpet_keep_vs_replace_card_download")}
                  className="mt-5 inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-5 py-3 hover:bg-primary-hover transition-colors"
                >
                  <Download className="h-4 w-4" /> Download the Card (PDF)
                </a>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="carpet-card-form" className="p-7">
                <p className="mono-label">Free download</p>
                <h3 className="mt-2 font-display text-xl font-medium leading-snug">Get the Carpet: Keep vs. Replace card</h3>
                <p className="mt-2 text-sm text-foreground/60 leading-relaxed">
                  Enter your email and we'll send the one-page, print-ready PDF and subscribe you to Greenpapers. Unsubscribe anytime.
                </p>
                <div className="mt-5 space-y-3">
                  <input
                    data-testid="carpet-card-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name (optional)"
                    className="w-full bg-surface border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                  />
                  <input
                    data-testid="carpet-card-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="w-full bg-surface border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                  />
                </div>
                {status.state === "error" && <p data-testid="carpet-card-error" className="mt-3 text-xs text-destructive">{status.message}</p>}
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid="carpet-card-submit"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-6 py-3.5 hover:bg-primary-hover transition-colors disabled:opacity-60 shadow-[0_0_20px_rgba(0,229,153,0.3)]"
                >
                  {status.state === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : <><Download className="h-4 w-4" /> Email me the Carpet Card</>}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
